import React, { useMemo } from 'react';
import classnames from 'classnames/bind';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import styles from './progress.module.scss';
const bStyles = classnames.bind(styles);

export const ProgressBar = ({
  bars = [],
  className,
  scale = 1,
  showTooltip,
  tooltip,
  name,
  percentPrecision = 0,
  showPercent = true,
  complete
}) => {
  const percent = useMemo(() => {
    const total = bars.reduce((sum, b) => sum + (b?.width || 0), 0);
    if (!scale) return 0;
    const p = (total / scale) * 100;
    const factor = Math.pow(10, percentPrecision);
    return Math.max(0, Math.min(100, Math.round(p * factor) / factor));
  }, [bars, scale, percentPrecision]);

  const rounded = useMemo(() => {
    const f = Math.pow(10, percentPrecision);
    return Math.round(percent * f) / f;
  }, [percent, percentPrecision]);

  const visible = bars?.filter(b => (b?.width || 0) > 0);

  return (
    <div
      className={bStyles(className, 'container')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
      aria-label={name || 'progress'}
      role="progressbar"
    >
      {showTooltip && (
        <ReactTooltip className="whiteTheme" id={name}>
          {tooltip}
        </ReactTooltip>
      )}
      <div className={styles.bar} data-tip="whiteTheme" data-tooltip-id={name}>
        {visible.map((bar, i) => {
          if (!bar.width) {
            return <React.Fragment key={i} />;
          }
          return (
            <div
              key={i}
              className={bStyles('barPiece', bar.color, {
                first: i === 0,
                second: i === 1,
                last: i === visible?.length - 1,
                single: visible?.length === 1
              })}
              style={{ width: `${(bar.width / scale) * 100}%` }}
            />
          );
        })}
        {/* added this then realized it wouldn't work with how incidents handle percentages 
        so I'm leaving it in case there's ever a use for it - but we'll have to remove the && complete render check */}
        {showPercent && complete ? (
          <div
            className={bStyles('percentInside', { complete })}
            style={{ '--p': `${rounded}%` }}
            aria-hidden="true"
          >
            {percent.toFixed(percentPrecision)}%
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProgressBar;
